import type { InterventionTaskViewModel } from '@generated';
import { Banner } from '~/shared/components/Banner';
import {
	ActivitiesPermitted,
	FunctionalLimitations,
	MentalStatus,
	NutritionalRequirements,
	Prognosis,
	RehabPotential,
	SafetyMeasures
} from './locator-items/LocatorItemSections';

const locatorSystemNames = {
	prognosis: '20481000',
	rehabPotential: 'PLANOFCAREREHABPOTENTIAL',
	mentalStatus: 'PLANOFCAREMENTALSTATUS',
	activitiesPermitted: 'PLANOFCAREACTIVITIESPERMITTED',
	functionalLimitations: 'PLANOFCAREFUNCTIONALLIMITATIONS',
	nutritionlRequirements: 'PLANOFCARENUTRITIONALREQUIREMENTS',
	safetyMeasure: 'PLANOFCARESAFETYMEASURES'
};

function getLocatorItemBySystemName(planOfCareLocatorItems: InterventionTaskViewModel[], systemName: string) {
	return planOfCareLocatorItems.find(x => x.interventionDefinition && x.interventionDefinition.systemName === systemName);
}

export function PlanOfCareLocatorItems({ planOfCareLocatorItems }: { planOfCareLocatorItems: InterventionTaskViewModel[] }) {
	const unresolved = planOfCareLocatorItems.filter(x => !x.resolved);
	if (unresolved.length > 0) {
		return (
			<Banner
				intent="warning"
				title="Unresolved Assessments"
				message="There are outstanding assessments that need to be completed before a plan of care can be generated."
			/>
		);
	}

	const prognosis = getLocatorItemBySystemName(planOfCareLocatorItems, locatorSystemNames.prognosis);
	const rehabPotential = getLocatorItemBySystemName(planOfCareLocatorItems, locatorSystemNames.rehabPotential);
	const mentalStatus = getLocatorItemBySystemName(planOfCareLocatorItems, locatorSystemNames.mentalStatus);
	const activitiesPermitted = getLocatorItemBySystemName(planOfCareLocatorItems, locatorSystemNames.activitiesPermitted);
	const functionalLimitations = getLocatorItemBySystemName(planOfCareLocatorItems, locatorSystemNames.functionalLimitations);
	const nutritionlRequirements = getLocatorItemBySystemName(planOfCareLocatorItems, locatorSystemNames.nutritionlRequirements);
	const safetyMeasure = getLocatorItemBySystemName(planOfCareLocatorItems, locatorSystemNames.safetyMeasure);
	if (
		!prognosis ||
		!rehabPotential ||
		!mentalStatus ||
		!activitiesPermitted ||
		!functionalLimitations ||
		!nutritionlRequirements ||
		!safetyMeasure
	) {
		const missingItems = [
			!prognosis && 'Prognosis',
			!rehabPotential && 'Rehab Potential',
			!mentalStatus && 'Mental Status',
			!activitiesPermitted && 'Activities Permitted',
			!functionalLimitations && 'Functional Limitations',
			!nutritionlRequirements && 'Nutritional Requirements',
			!safetyMeasure && 'Safety Measures'
		]
			.filter(Boolean)
			.join(', ');
		return (
			<Banner
				intent="warning"
				title="Incomplete Assessments"
				message={`The following assessments are missing and need to be completed before a plan of care can be generated: ${missingItems}.`}
			/>
		);
	}

	return (
		<div className="grid grid-cols-2 gap-5">
			<div className="space-y-5">
				<div className="grid grid-cols-2 gap-5">
					<Prognosis interventionTask={prognosis} />
					<RehabPotential interventionTask={rehabPotential} />
				</div>
				<MentalStatus interventionTask={mentalStatus} />
				<ActivitiesPermitted interventionTask={activitiesPermitted} />
			</div>
			<div className="space-y-5">
				<FunctionalLimitations interventionTask={functionalLimitations} />
				<NutritionalRequirements interventionTask={nutritionlRequirements} />
				<SafetyMeasures interventionTask={safetyMeasure} />
			</div>
		</div>
	);
}
